import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./NewAppAccessLevelPage.module.scss";
import CustomInput from "../../components/Inputs/CustomInput";
import Modal from "../../components/Modal/Modal";
import { AxiosErrorType } from "../../types";

interface FormData {
  name: string;
  permissions: string;
}

export default function NewAppAccessLevelPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    permissions: "{}",
  });
  const [modalMessage, setModalMessage] = useState<string>("");
  const [modalIsError, setModalIsError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!formData.name) {
      setModalMessage("Please provide a name for the access level.");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    let permissionsObj: Record<string, boolean> = {};
    try {
      permissionsObj = JSON.parse(formData.permissions);
      if (typeof permissionsObj !== "object" || Array.isArray(permissionsObj)) {
        throw new Error("Permissions must be a valid JSON object.");
      }
    } catch (err) {
      setModalMessage("Invalid permissions format. Please provide a valid JSON object (e.g., {\"read\": true, \"write\": false}).");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      console.log("[NewAppAccessLevelPage] Creating access level:", { name: formData.name, permissions: permissionsObj });
      await api.post("/core/role/access-level/create", {
        name: formData.name,
        permissions: permissionsObj,
      });
      console.log("[NewAppAccessLevelPage] Access level created");
      setModalMessage("Access level created successfully.");
      setModalIsError(false);
      setShowModal(true);
      setTimeout(() => navigate("/app-access-levels"), 2000);
    } catch (err: AxiosErrorType) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to create access level";
      console.error("[NewAppAccessLevelPage] Failed to create access level:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>Create New App Access Level</div>
      <div className={styles.section}>
        <div className={styles.formGroup}>
          <CustomInput
            label="Access Level Name"
            value={formData.name}
            onChange={(val: string) => setFormData({ ...formData, name: val })}
            type="text"
            placeholder="e.g., Admin"
            size="full"
            required
          />
          <CustomInput
            label="Permissions (JSON)"
            value={formData.permissions}
            onChange={(val: string) => setFormData({ ...formData, permissions: val })}
            type="text"
            placeholder='e.g., {"read": true, "write": false}'
            size="full"
          />
        </div>
        <div className={styles.actions}>
          <button
            onClick={handleSubmit}
            className={styles.actionButton}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create"}
          </button>
          <button
            onClick={() => navigate("/app-access-levels")}
            className={styles.actionButton}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        message={modalMessage}
        isError={modalIsError}
      />
    </div>
  );
}
